import { AgentConversationList } from "./components/agent-conversion-list";
import { AgentConversationBox } from "./components/agent-conversation-box";
import { useTheme } from "components/core/theme-provider";
import { cn } from "lib/utils";
import { useEffect, useRef, useState } from "react";
import { useQueryState } from "nuqs";
import { useTranslation } from "react-i18next";

import { useAgentConversationStore } from "./utils/agent-conversation-store";
import { useMediaQuery } from "hooks/use-media-query";
import { getPickerTheme } from "pages/agents/utils/agent-config-picker-theme";

type TabType = "public" | "private";

export const AgentConversation = ({ agentId }: { agentId: string }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const ui = getPickerTheme(theme === "dark");
  const [sessionId] = useQueryState("session_id", {
    defaultValue: "",
  });
  const [detailsPanel, setDetailsPanel] = useQueryState("details_panel");
  const prevSessionIdRef = useRef(sessionId);

  const [selectedConversationTitle, setSelectedConversationTitle] = useState("");
  const [selectedConversationUser, setSelectedConversationUser] = useState("");
  const [activeTab, setActiveTab] = useState<TabType>("public");

  useEffect(() => {
    if (prevSessionIdRef.current !== sessionId) {
      setDetailsPanel(null);
      prevSessionIdRef.current = sessionId;
    }
  }, [sessionId, setDetailsPanel]);

  const { detailsWidth } = useAgentConversationStore();
  const isDesktop = useMediaQuery("(min-width: 768px)");

  return (
    <div
      data-testid="agent-conversations-workspace"
      className="flex w-full items-stretch gap-4 md:h-full md:min-h-0 md:flex-1 lg:gap-5"
    >
      <div
        className={cn(
          "flex min-h-[calc(100dvh-13rem)] w-full min-w-0 max-w-full flex-col overflow-hidden rounded-3xl border shadow-sm md:h-full md:min-h-0 md:w-[35%] md:max-w-96 md:flex-none lg:max-h-[calc(100dvh-8rem)]",
          ui.dialog,
          sessionId && "hidden md:flex",
        )}
      >
        <div className={cn("shrink-0 px-4 pb-4 pt-5 sm:pt-6", ui.header)}>
          <h2 className={cn("text-2xl font-bold leading-tight tracking-tight sm:text-[1.65rem]", ui.title)}>
            {t("CONVERSATIONS", { defaultValue: "Conversations" })}
          </h2>
        </div>
        <div className="min-h-0 flex-1">
          <AgentConversationList
            agentId={agentId}
            setSelectedConversationTitle={setSelectedConversationTitle}
            setSelectedConversationUser={setSelectedConversationUser}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        </div>
      </div>
      <div
        className={cn(
          "hidden min-h-0 min-w-0 flex-1 transition-all duration-300 ease-in-out md:flex",
          sessionId && "flex",
        )}
        style={{ marginRight: detailsPanel && isDesktop ? `${detailsWidth}px` : "0px" }}
      >
        <div
          className={cn(
            "flex min-h-[calc(100dvh-16rem)] w-full min-w-0 flex-col overflow-hidden rounded-3xl border shadow-sm md:h-full md:min-h-0 lg:max-h-[calc(100dvh-8rem)]",
            ui.dialog,
          )}
        >
          <AgentConversationBox
            agentId={agentId}
            title={selectedConversationTitle}
            username={selectedConversationUser}
            isPrivate={activeTab === "private"}
          />
        </div>
      </div>
    </div>
  );
};
